import { LOGIC_ACTION } from '@epilot/journey-logic-commons/src/types/next'
import isDeepEqual from 'fast-deep-equal'
import { original } from 'immer'
import { nanoid } from 'nanoid'

import {
  selectInitiallyShownBlocks,
  selectLastDesiredState
} from '../selectors/runtime'
import type { DesiredState } from '../types'
import type { PushLogicEventsAction } from '../types/actions'

import { actionFactory } from './factory'

/**
 * Determines the maximum number of items to keep in the history.
 * Prevents the history from growing unbounded.
 */
const ENGINE_HISTORY_LIMIT = 10

export const pushLogicEvents = actionFactory<PushLogicEventsAction>(
  (state, { events }) => {
    let lastDesiredState = selectLastDesiredState(state)

    if (lastDesiredState) {
      lastDesiredState = original(lastDesiredState)
    }

    // skip if the events are the same as the last ones
    if (lastDesiredState && isDeepEqual(events, lastDesiredState.events)) return

    // compute the new desired state
    const newDesiredState = events.reduce<DesiredState>(
      (acc, event) => {
        switch (event.action) {
          case LOGIC_ACTION.DISPLAY_CONDITION:
            acc.show.add(event.details.targetBlock)
            break
          case LOGIC_ACTION.SKIP_STEP:
            event.details.skipSteps.forEach(acc.skipSteps.add, acc.skipSteps)
            break
          case LOGIC_ACTION.JUMP_TO_STEP:
            // ensures the highest priority event always wins
            // this is necesary for cases where the order of precedence is important
            // (i.e. availability check functions)
            if (typeof acc.nextStepOverride === 'undefined')
              acc.nextStepOverride = event.details.targetStep
            break
        }

        return acc
      },
      {
        key: nanoid(8),
        events,
        show: selectInitiallyShownBlocks(state),
        skipSteps: new Set() // starts empty
      }
    )

    // append the new value to the history while making sure it doesn't grow unbounded
    state.logicsEngine.history = [
      ...state.logicsEngine.history.slice(1 - ENGINE_HISTORY_LIMIT),
      newDesiredState
    ]
  },
  'pushLogicEvents'
)
